import { addDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import type { DateFilterKey, DateFilterRange } from './types';

export const APPLICATION_TIMEZONE = 'America/Sao_Paulo';

export const formatScheduledDate = (value: string): string =>
  formatInTimeZone(value, APPLICATION_TIMEZONE, 'dd/MM/yyyy');

export const getScheduledDate = (value: string | null): string =>
  value ? formatInTimeZone(value, APPLICATION_TIMEZONE, 'yyyy-MM-dd') : '';

export const formatScheduledTime = (value: string): string =>
  formatInTimeZone(value, APPLICATION_TIMEZONE, 'HH:mm');

export const formatDuration = (minutes?: number | null): string => {
  if (minutes == null) return '—';
  if (minutes < 60) return `${minutes}min`;

  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;

  return remaining === 0
    ? `${hours}h`
    : `${hours}h${String(remaining).padStart(2, '0')}`;
};

export const parseTimeToMinutes = (value: string): number | null => {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;

  return hours * 60 + minutes;
};

export const isScheduledDateInThePast = (value: string | null): boolean => {
  const date = getScheduledDate(value);
  if (!date) return false;
  const today = formatInTimeZone(
    new Date(),
    APPLICATION_TIMEZONE,
    'yyyy-MM-dd'
  );
  return date < today;
};

export const buildDateFilterRange = (key: DateFilterKey): DateFilterRange => {
  const today = new Date();
  const asDay = (date: Date) =>
    formatInTimeZone(date, APPLICATION_TIMEZONE, 'yyyy-MM-dd');

  switch (key) {
    case 'today':
      return { dateFrom: asDay(today), dateTo: asDay(today) };
    case 'tomorrow': {
      const tomorrow = addDays(today, 1);
      return { dateFrom: asDay(tomorrow), dateTo: asDay(tomorrow) };
    }
    case 'next7':
      return { dateFrom: asDay(today), dateTo: asDay(addDays(today, 6)) };
    case 'fromToday':
      return { dateFrom: asDay(today) };
    default:
      return {};
  }
};
